import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, ImageOverlay, Marker, Tooltip, Polygon, useMap } from "react-leaflet";
import axios from "axios";
import { API } from "../App";
import { createCustomIcon } from "../components/map/mapIcons";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Map as MapIcon } from "lucide-react";
import "../styles/print-map.css";

// Auto-zoom hjælper: når overlay bounds er klar, zoom til dem
function FitBoundsHelper({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && map) {
      try {
        map.fitBounds(bounds, { padding: [40, 40] });
      } catch (e) { /* ignore */ }
    }
  }, [bounds, map]);
  return null;
}

/**
 * Printvenlig kort-side
 *
 * - Lyse farver (sparer printer-blæk)
 * - UI-elementer skjules ved print
 * - Ressourceøer + maxicontainere får synlige labels (point.name) ved siden af ikon
 * - Skraldespande/service-punkter rendres uden labels
 * - Header med festival-navn + dato
 *
 * Brug: window.open('/print-map?event_id=XXX') eller knap fra Analytics
 */
export default function PrintMap({ user, selectedEvent }) {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const eventId = selectedEvent?.id || new URLSearchParams(window.location.search).get('event_id');
  const eventName = selectedEvent?.name || '';

  const [points, setPoints] = useState([]);
  const [maxicontainers, setMaxicontainers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [overlay, setOverlay] = useState(null);
  const [defaultLocation, setDefaultLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orientation, setOrientation] = useState('landscape');
  const [showLabels, setShowLabels] = useState(true);

  useEffect(() => {
    if (!eventId) return;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/resource-points?event_id=${eventId}`, { headers }),
      axios.get(`${API}/maxicontainers?event_id=${eventId}`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/areas?event_id=${eventId}`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/map-overlay?event_id=${eventId}`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API}/events/${eventId}/default-location`, { headers }).catch(() => ({ data: null })),
    ]).then(([p, mc, a, ov, dl]) => {
      setPoints(p.data || []);
      setMaxicontainers(mc.data || []);
      setAreas(a.data || []);
      setOverlay(ov.data || null);
      setDefaultLocation(dl.data || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [eventId, token]);

  const overlayBounds = useMemo(() => {
    if (overlay?.bounds && Array.isArray(overlay.bounds) && overlay.bounds.length === 2) {
      return overlay.bounds;  // [[south, west], [north, east]]
    }
    if (overlay?.bounds && typeof overlay.bounds.south === 'number' && !isNaN(overlay.bounds.south)) {
      return [[overlay.bounds.south, overlay.bounds.west], [overlay.bounds.north, overlay.bounds.east]];
    }
    return null;
  }, [overlay]);

  const mapCenter = useMemo(() => {
    if (defaultLocation && typeof defaultLocation.lat === 'number' && !isNaN(defaultLocation.lat)) {
      return [defaultLocation.lat, defaultLocation.lng];
    }
    if (overlayBounds) {
      return [(overlayBounds[0][0] + overlayBounds[1][0]) / 2, (overlayBounds[0][1] + overlayBounds[1][1]) / 2];
    }
    return [55.6761, 12.5683];  // Fallback København
  }, [defaultLocation, overlayBounds]);

  const mapZoom = (defaultLocation && typeof defaultLocation.zoom === 'number') ? defaultLocation.zoom : 16;

  const handlePrint = () => {
    document.documentElement.classList.add('print-mode');
    setTimeout(() => {
      window.print();
      setTimeout(() => document.documentElement.classList.remove('print-mode'), 500);
    }, 300);
  };

  // Hvilke punkter får labels: ressourceø + maxicontainer
  const shouldShowLabel = (type) => ['ressourceø', 'maxicontainer'].includes(type);

  if (loading) {
    return <div className="h-screen w-screen flex items-center justify-center bg-zinc-950 text-white">Henter kort-data...</div>;
  }

  return (
    <div className="h-screen w-screen flex flex-col bg-white print-map-container" data-orientation={orientation}>
      {/* Toolbar — skjules ved print */}
      <div className="bg-zinc-900 text-white px-4 py-3 flex flex-wrap items-center justify-between gap-3 no-print" data-testid="print-toolbar">
        <div className="flex items-center gap-3 flex-shrink-0">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="print-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Tilbage
          </Button>
          <h1 className="text-sm font-mono uppercase font-bold">Printvenlig udskrift</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-xs whitespace-nowrap">
            <input type="checkbox" checked={showLabels} onChange={(e) => setShowLabels(e.target.checked)} className="h-4 w-4" data-testid="toggle-labels" />
            Vis navne
          </label>
          <select value={orientation} onChange={(e) => setOrientation(e.target.value)} className="bg-zinc-800 text-white text-xs rounded px-2 py-1" data-testid="orientation-select">
            <option value="landscape">Liggende (A4/A3)</option>
            <option value="portrait">Stående (A4/A3)</option>
          </select>
          <Button size="sm" onClick={handlePrint} className="bg-green-600 hover:bg-green-700 text-white" data-testid="print-btn">
            <Printer className="h-4 w-4 mr-1" /> Udskriv
          </Button>
        </div>
      </div>

      {/* Print header — kun synlig ved print */}
      <div className="print-only px-4 py-2 border-b border-zinc-300">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-bold">RESOVIA · OVERSIGTSKORT</h1>
            {eventName && <p className="text-sm">{eventName}</p>}
          </div>
          <div className="text-xs text-right">
            <div>Printet: {new Date().toLocaleString('da-DK', { dateStyle: 'long', timeStyle: 'short' })}</div>
            <div>{points.filter(p => p.point_type === 'ressourceø').length} ressourceøer · {maxicontainers.length} maxicontainere</div>
          </div>
        </div>
      </div>

      {/* Kort-område */}
      <div className="flex-1 relative print-map-wrapper bg-white">
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          maxZoom={22}
          minZoom={3}
          className="h-full w-full"
          zoomControl={false}
          attributionControl={false}
        >
          {/* Lys baggrund-tile (i stedet for mørk) */}
          <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />

          {/* Auto-zoom til overlay bounds når data er klar */}
          {overlayBounds && <FitBoundsHelper bounds={overlayBounds} />}

          {overlay && overlayBounds && (
            <ImageOverlay
              key={`overlay-${overlay.id}-r${overlay.rotation_applied || 0}`}
              url={`${API}/map-overlay/${overlay.id}/image?v=${overlay.rotation_applied || 0}`}
              bounds={overlayBounds}
              opacity={overlay.opacity || 0.7}
            />
          )}

          {/* Områder */}
          {areas.filter(a => a.corners?.length > 2).map(a => (
            <Polygon
              key={a.id}
              positions={a.corners.map(c => [c.lat, c.lng])}
              pathOptions={{ color: '#16a34a', weight: 2, fillOpacity: 0, dashArray: '5,3' }}
            />
          ))}

          {/* Ressourcepunkter (alle typer) */}
          {points.filter(p => p.lat && p.lng).map(point => (
            <Marker
              key={point.id}
              position={[point.lat, point.lng]}
              icon={createCustomIcon(point.point_type, 'grøn', point.bin_fraction, null, point.bins, mapZoom, point.containers, [])}
            >
              {showLabels && shouldShowLabel(point.point_type) && (
                <Tooltip permanent direction="right" offset={[10, 0]} className="print-label">
                  <span className="text-[11px] font-bold">{point.name}</span>
                </Tooltip>
              )}
            </Marker>
          ))}

          {/* Maxicontainere */}
          {maxicontainers.filter(m => m.lat && m.lng).map(m => (
            <Marker
              key={m.id}
              position={[m.lat, m.lng]}
              icon={createCustomIcon('maxicontainer', 'grøn', null, null, null, mapZoom, [], [])}
            >
              {showLabels && (
                <Tooltip permanent direction="right" offset={[10, 0]} className="print-label">
                  <span className="text-[11px] font-bold">{m.name}</span>
                </Tooltip>
              )}
            </Marker>
          ))}
        </MapContainer>

        {/* Tegnforklaring (synlig både på skærm og ved print) */}
        <div className="absolute bottom-4 left-4 bg-white/95 border border-zinc-300 rounded px-3 py-2 text-[11px] shadow print-legend" data-testid="print-legend">
          <div className="font-bold mb-1 uppercase tracking-wide">Tegnforklaring</div>
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-green-500" /> Ressourceø</div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-blue-500" /> Skraldespand</div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-orange-500" /> Maxicontainer</div>
            <div className="flex items-center gap-2"><MapIcon className="w-3 h-3 text-green-700" /> Område</div>
          </div>
        </div>
      </div>
    </div>
  );
}
